import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import BudgetSitesLanding from "@/components/BudgetSitesLanding";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CaretRight, CaretDown, TrendUp, TrendDown, ChartBar, CurrencyCircleDollar,
  X, ArrowsCounterClockwise, ArrowLeft, MapPin,
} from "@phosphor-icons/react";

const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", maximumFractionDigits: 2,
  }).format(Number(n) || 0);

/**
 * Ana İcmal (Kâr/Zarar) — Excel'in AnaIcmal sekmesinin canlı versiyonu.
 * Hiyerarşik toplamlar: parent düğüm = kendi kayıtları + tüm alt düğüm toplamları.
 */
export default function BudgetProfitLoss() {
  const { sites, activeSiteId, setActiveSiteId } = useSites();
  const [data, setData] = useState({ categories: [], totals: {} });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(false);

  const currentSiteId = activeSiteId && activeSiteId !== "all" ? activeSiteId : "";
  const currentSite = sites.find((s) => s.id === currentSiteId);
  const showSiteLanding = !currentSiteId;

  const load = async () => {
    if (!currentSiteId) { setData({ categories: [], totals: {} }); return; }
    setLoading(true);
    try {
      const params = {};
      if (currentSiteId) params.site_id = currentSiteId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/budget/summary`, { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [currentSiteId, dateFrom, dateTo]); // eslint-disable-line

  // Hiyerarşik toplamları hesapla: her düğüm için kendi + tüm alt düğümlerin toplamı
  const withSubtotals = useMemo(() => {
    const byCode = Object.fromEntries(data.categories.map((c) => [c.code, { ...c }]));
    const childrenOf = {};
    data.categories.forEach((c) => {
      const p = c.parent_code || "__root__";
      (childrenOf[p] = childrenOf[p] || []).push(c.code);
    });
    // Faz 34.54: Her seviyede gelir üstte, sonra doğal alfabetik sıra (WBS gibi)
    const sortChildren = (a, b) => {
      const ca = byCode[a]; const cb = byCode[b];
      const kindA = ca?.kind === "gelir" ? 0 : 1;
      const kindB = cb?.kind === "gelir" ? 0 : 1;
      if (kindA !== kindB) return kindA - kindB;
      return (ca?.code || "").localeCompare(cb?.code || "", "tr", {
        numeric: true, sensitivity: "base",
      });
    };
    Object.keys(childrenOf).forEach((k) => childrenOf[k].sort(sortChildren));
    // Recursive toplamlar
    const computed = {};
    const compute = (code) => {
      if (computed[code]) return computed[code];
      const self = byCode[code];
      const kids = childrenOf[code] || [];
      let noVat = Number(self.gercek_no_vat) || 0;
      let withVat = Number(self.gercek_with_vat) || 0;
      let planNoVat = Number(self.tahmin_no_vat) || 0;
      let planWithVat = Number(self.tahmin_with_vat) || 0;
      let resmiNoVat = Number(self.resmi_no_vat) || 0;
      let resmiWithVat = Number(self.resmi_with_vat) || 0;
      let gayriNoVat = Number(self.gayri_no_vat) || 0;
      let gayriWithVat = Number(self.gayri_with_vat) || 0;
      let count = Number(self.count) || 0;
      kids.forEach((childCode) => {
        const ch = compute(childCode);
        noVat += ch.subtotal_no_vat;
        withVat += ch.subtotal_with_vat;
        planNoVat += ch.subtotal_plan_no_vat;
        planWithVat += ch.subtotal_plan_with_vat;
        resmiNoVat += ch.subtotal_resmi_no_vat;
        resmiWithVat += ch.subtotal_resmi_with_vat;
        gayriNoVat += ch.subtotal_gayri_no_vat;
        gayriWithVat += ch.subtotal_gayri_with_vat;
        count += ch.subtotal_count;
      });
      const res = {
        ...self,
        subtotal_no_vat: noVat,
        subtotal_with_vat: withVat,
        subtotal_plan_no_vat: planNoVat,
        subtotal_plan_with_vat: planWithVat,
        subtotal_resmi_no_vat: resmiNoVat,
        subtotal_resmi_with_vat: resmiWithVat,
        subtotal_gayri_no_vat: gayriNoVat,
        subtotal_gayri_with_vat: gayriWithVat,
        subtotal_count: count,
        has_children: kids.length > 0,
      };
      computed[code] = res;
      return res;
    };
    data.categories.forEach((c) => compute(c.code));
    return { computed, childrenOf };
  }, [data]);

  // Hiyerarşik render
  const renderNode = (code, depth) => {
    const node = withSubtotals.computed[code];
    if (!node) return null;
    const kids = withSubtotals.childrenOf[code] || [];
    const isCollapsed = collapsed[code];
    const isGelir = node.kind === "gelir";
    const isRoot = depth === 0;
    // Skip empty branches if user prefers (only show branches with any activity)
    // — for now, show all
    const bgColor = isRoot
      ? (isGelir ? "bg-emerald-50/60" : "bg-rose-50/60")
      : "";
    const boldClass = isRoot ? "font-bold" : depth === 1 ? "font-semibold" : "";
    return (
      <React.Fragment key={code}>
        <tr className={`${bgColor} border-b border-slate-100 hover:bg-slate-50/70`}>
          <td className="px-4 py-2" style={{ paddingLeft: 12 + depth * 18 }}>
            <div className="flex items-center gap-2">
              {node.has_children ? (
                <button onClick={() => setCollapsed((c) => ({ ...c, [code]: !isCollapsed }))}
                        className="text-slate-400 hover:text-[#1B4776]"
                        data-testid={`pl-toggle-${code}`}>
                  {isCollapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                </button>
              ) : (
                <span className="w-3.5" />
              )}
              <span className={`font-mono text-xs ${boldClass} ${
                isGelir ? "text-emerald-700" : "text-[#1B4776]"
              }`}>{node.code}</span>
            </div>
          </td>
          <td className={`px-4 py-2 text-slate-800 ${boldClass}`}>
            {node.name}
          </td>
          <td className={`px-4 py-2 text-right font-mono text-xs ${
            isGelir ? "text-emerald-700" : "text-slate-700"
          } ${boldClass}`}>
            {node.subtotal_no_vat ? fmtTL(node.subtotal_no_vat) : "—"}
          </td>
          <td className={`px-4 py-2 text-right font-mono text-xs ${
            isGelir ? "text-emerald-800" : "text-rose-700"
          } ${boldClass}`}>
            {node.subtotal_with_vat ? fmtTL(node.subtotal_with_vat) : "—"}
          </td>
          <td className={`px-4 py-2 text-right font-mono text-xs ${boldClass}`}>
            {node.subtotal_resmi_with_vat
              ? <span className="text-emerald-700">{fmtTL(node.subtotal_resmi_with_vat)}</span>
              : <span className="text-slate-300">—</span>}
          </td>
          <td className={`px-4 py-2 text-right font-mono text-xs ${boldClass}`}>
            {node.subtotal_gayri_with_vat
              ? <span className="text-amber-800 bg-amber-50 px-1.5 py-0.5">{fmtTL(node.subtotal_gayri_with_vat)}</span>
              : <span className="text-slate-300">—</span>}
          </td>
          <td className="px-4 py-2 text-right font-mono text-xs text-slate-400">
            {node.subtotal_plan_with_vat ? fmtTL(node.subtotal_plan_with_vat) : "—"}
          </td>
          <td className="px-4 py-2 text-right text-xs text-slate-400">
            {node.subtotal_count || "—"}
          </td>
        </tr>
        {!isCollapsed && kids.map((k) => renderNode(k, depth + 1))}
      </React.Fragment>
    );
  };

  const rootCodes = (withSubtotals.childrenOf["__root__"] || []);

  // Alt karşılaştırma toplamları
  const gelirRoots = rootCodes.filter((c) => withSubtotals.computed[c]?.kind === "gelir");
  const giderRoots = rootCodes.filter((c) => withSubtotals.computed[c]?.kind === "gider");
  const totalGelirNoVat = gelirRoots.reduce((a, c) => a + (withSubtotals.computed[c]?.subtotal_no_vat || 0), 0);
  const totalGelirWithVat = gelirRoots.reduce((a, c) => a + (withSubtotals.computed[c]?.subtotal_with_vat || 0), 0);
  const totalGiderNoVat = giderRoots.reduce((a, c) => a + (withSubtotals.computed[c]?.subtotal_no_vat || 0), 0);
  const totalGiderWithVat = giderRoots.reduce((a, c) => a + (withSubtotals.computed[c]?.subtotal_with_vat || 0), 0);
  const brutKar = totalGelirNoVat - totalGiderWithVat;

  return (
    <Shell title="Kâr / Zarar (Ana İcmal)" subtitle="Bütçe Yönetimi">
      {showSiteLanding ? (
        <BudgetSitesLanding
          title="Kâr / Zarar (Ana İcmal)"
          subtitle="Kâr-zarar özetini görüntülemek için önce bir şantiye seçin."
          icon={ChartBar}
          accent="indigo"
          onSelectSite={(sid) => setActiveSiteId(sid)}
          testIdPrefix="pl"
        />
      ) : (
      <>
      {/* Şantiye Seçim Header */}
      <div className="flex items-center justify-between gap-4 p-3 mb-3 bg-[#F7FBE9] border border-[#95C11F] rounded-none sm:rounded-sm"
           data-testid="pl-site-scope-header">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => setActiveSiteId("all")}
                  className="p-1.5 rounded-full bg-white border border-[#95C11F] text-[#556B10] hover:bg-[#95C11F]/10 transition"
                  data-testid="pl-back-to-index">
            <ArrowLeft size={14} weight="bold" />
          </button>
          <MapPin size={18} weight="duotone" className="text-[#556B10] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#556B10] font-bold">
              Aktif Şantiye
            </div>
            <div className="text-sm font-bold text-[#1B4776] truncate">
              <span className="font-mono">{currentSite?.code || currentSiteId}</span>
              {currentSite?.name && <span className="ml-2 text-slate-700">— {currentSite.name}</span>}
            </div>
          </div>
        </div>
        <button onClick={() => setActiveSiteId("all")}
                className="text-xs text-[#556B10] hover:underline shrink-0 font-semibold"
                data-testid="pl-change-site">
          Şantiye Değiştir
        </button>
      </div>

      {/* KPI Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Toplam Gelir (KDV Hariç)</div>
            <TrendUp size={16} weight="duotone" className="text-emerald-600" />
          </div>
          <div className="text-lg font-bold text-emerald-700 mt-1">{fmtTL(totalGelirNoVat)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">KDV Dahil: {fmtTL(totalGelirWithVat)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold">Toplam Gider (KDV Dahil)</div>
            <TrendDown size={16} weight="duotone" className="text-rose-600" />
          </div>
          <div className="text-lg font-bold text-rose-700 mt-1">{fmtTL(totalGiderWithVat)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">KDV Hariç: {fmtTL(totalGiderNoVat)}</div>
        </Card>
        <Card className={`p-4 rounded-none sm:rounded-sm border-slate-200 ${
          brutKar >= 0 ? "bg-[#F7FBE9] border-[#95C11F]" : "bg-rose-50 border-rose-300"
        }`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">Brüt Kâr</div>
            <CurrencyCircleDollar size={16} weight="duotone" className={brutKar >= 0 ? "text-[#556B10]" : "text-rose-700"} />
          </div>
          <div className={`text-lg font-bold mt-1 ${brutKar >= 0 ? "text-[#556B10]" : "text-rose-700"}`}>
            {fmtTL(brutKar)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Gelir(KDV Hariç) - Gider(KDV Dahil)</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Kâr Marjı</div>
            <ChartBar size={16} weight="duotone" className="text-slate-500" />
          </div>
          <div className={`text-lg font-bold mt-1 ${brutKar >= 0 ? "text-[#1B4776]" : "text-rose-700"}`}>
            {totalGelirNoVat ? ((brutKar / totalGelirNoVat) * 100).toFixed(1) : "0.0"}%
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">(Brüt Kâr / Gelir)</div>
        </Card>
      </div>

      {/* Filtre */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tarih:</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="rounded-none sm:rounded-sm w-40 h-8 text-xs" data-testid="pl-date-from" />
            <span className="text-slate-400 text-xs">—</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="rounded-none sm:rounded-sm w-40 h-8 text-xs" data-testid="pl-date-to" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="text-slate-400 hover:text-rose-600 p-1" title="Tarih filtrelerini temizle">
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
          <Button variant="outline" onClick={load} disabled={loading}
                  className="ml-auto rounded-none sm:rounded-sm h-8 text-xs"
                  data-testid="pl-refresh">
            <ArrowsCounterClockwise size={12} weight="bold" className="mr-1" />
            Yenile
          </Button>
        </div>
      </Card>

      {/* Ana İcmal Tablosu */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b-2 border-slate-300 bg-slate-100">
                <th className="px-4 py-3 text-left w-48">Kod</th>
                <th className="px-4 py-3 text-left">Kalem</th>
                <th className="px-4 py-3 text-right w-32">KDV Hariç</th>
                <th className="px-4 py-3 text-right w-32">KDV Dahil</th>
                <th className="px-4 py-3 text-right w-32 text-emerald-700">Resmi</th>
                <th className="px-4 py-3 text-right w-32 text-amber-700">Gayri Resmi</th>
                <th className="px-4 py-3 text-right w-32">Tahmin</th>
                <th className="px-4 py-3 text-right w-20">Kayıt</th>
              </tr>
            </thead>
            <tbody data-testid="pl-table-body">
              {rootCodes.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                    <ChartBar size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">CBS ağacı boş görünüyor.</p>
                  </td>
                </tr>
              )}
              {rootCodes.map((code) => renderNode(code, 0))}

              {/* Genel Toplam */}
              {rootCodes.length > 0 && (
                <>
                  <tr className="border-t-2 border-slate-300 bg-slate-100">
                    <td className="px-4 py-3 font-mono text-xs font-bold text-emerald-700">TOPLAM GELİR</td>
                    <td className="px-4 py-3 font-bold text-slate-800">Toplam Gelir (Hakediş vb.)</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-bold text-emerald-700">
                      {fmtTL(totalGelirNoVat)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-bold text-emerald-800">
                      {fmtTL(totalGelirWithVat)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">
                      {fmtTL(data.totals?.gelir_resmi || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-amber-800">
                      {(data.totals?.gelir_gayri || 0) > 0
                        ? <span className="bg-amber-50 px-1.5 py-0.5">{fmtTL(data.totals.gelir_gayri)}</span>
                        : "—"}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3" />
                  </tr>
                  <tr className="bg-slate-100">
                    <td className="px-4 py-3 font-mono text-xs font-bold text-rose-700">TOPLAM GİDER</td>
                    <td className="px-4 py-3 font-bold text-slate-800">Toplam Gider (Tüm Kalemler)</td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-bold text-slate-700">
                      {fmtTL(totalGiderNoVat)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-bold text-rose-700">
                      {fmtTL(totalGiderWithVat)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">
                      {fmtTL(data.totals?.gider_resmi || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-amber-800">
                      {(data.totals?.gider_gayri || 0) > 0
                        ? <span className="bg-amber-100 border border-amber-300 px-1.5 py-0.5">{fmtTL(data.totals.gider_gayri)}</span>
                        : "—"}
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3" />
                  </tr>
                  <tr className={brutKar >= 0 ? "bg-[#F7FBE9] border-t-2 border-[#95C11F]"
                                              : "bg-rose-50 border-t-2 border-rose-300"}>
                    <td className="px-4 py-3.5 font-mono text-xs font-bold text-slate-800">BRÜT KÂR</td>
                    <td className={`px-4 py-3.5 font-bold ${brutKar >= 0 ? "text-[#556B10]" : "text-rose-700"}`}>
                      Brüt Kâr (Gelir KDV Hariç − Gider KDV Dahil)
                    </td>
                    <td className="px-4 py-3.5" />
                    <td className={`px-4 py-3.5 text-right font-mono text-base font-bold ${
                      brutKar >= 0 ? "text-[#556B10]" : "text-rose-700"
                    }`}>
                      {fmtTL(brutKar)}
                    </td>
                    <td className="px-4 py-3.5" colSpan={4} />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-400">
        Hiyerarşik toplamlar: üst düğümler kendi + alt düğüm toplamlarını gösterir.
      </div>
      </>
      )}
    </Shell>
  );
}
